/**
 * 渠道模型库（Alice 页面 providerModels 的复刻）：采购侧的唯一事实来源，路由只能引用这里已登记且启用的上游模型。
 *
 * 背景：每个渠道可用的上游模型先在这里登记（模型 ID、类型、刊例价、折扣），上游成本也由这里的报价算出：
 *   采购价 = 刊例价 × 厂商折扣率；没有报价就把成本记为未知，不当成 0。对应 Alice 的 /upstream-cost-prices。
 * 设计意图：
 *   - 版式对齐 Alice：说明块、四张摘要卡、筛选栏（搜索、渠道、只看缺报价、刷新公开刊例）、表格（缺报价整行标红）。
 *   - 报价弹窗里可查公开刊例（OpenRouter 缓存）一键填入；复杂价目（分时、图片 Token、视频）仍在「完整编辑」里改，一项不丢。
 *   - 登记弹窗选渠道后自动拉该渠道的模型目录做候选（拉不到不影响手填）。
 * 关键约束：只有 admin 能写；保存、删除都会生成新配置版本；仍被路由引用的条目后端拒绝删除；
 *   本仓没有「自营渠道」概念，缺报价一律标红，折扣为 0（合作免费）算已知成本。
 */
import { IconEdit, IconPlus, IconRefresh } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { catalogApi, type FetchedModel, type LibraryItem, type LibraryPatch, type VendorRef } from '@ap/api-client/admin-catalog'
import type { ChannelModel, ChannelModelInput, ModelKind } from '@ap/api-client/types'
import { A } from '@ap/i18n/admin'
import { AG_catalog as G, errorTextCat } from '@ap/i18n/admin-catalog'
import { C } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import { Button, ConfirmModal, Field, Form, Input, Modal, Notice, Select, fmtDateTime, useLoad, useToast } from '@ap/ui'
import { Toolbar } from '../alice/kit.tsx'
import { ActionMenu, InfoBox, Logo, SearchBox, SummaryCard, SummaryGrid, fmtContext } from '../alice/kit-catalog.tsx'
import { useAdmin } from '../session.tsx'
import { ChannelModelForm } from './catalog/ChannelModelForm.tsx'
import { KINDS, SYMBOL, isEmptyObj, num, orNull } from './catalog/shared.ts'

const SOURCE_TONE: Record<string, string> = { manual: 'badge-info', fetched: 'badge-ok', published: 'badge-ok', migrated: 'badge-info' }

/** 报价摘要：文字类「输入 / 输出」，图片每张；没有报价返回 null。 */
function quoteText(m: ChannelModel, tImageToken: string, tVideo: string): string | null {
  const sym = SYMBOL[m.price_currency]
  if (m.kind === 'image') {
    if (m.image_billing_mode === 'token') return tImageToken
    return m.list_per_image == null ? null : `${sym}${num(m.list_per_image)}`
  }
  if (m.kind === 'video') return isEmptyObj(m.video_pricing) ? null : tVideo
  if (m.list_prompt == null && m.list_completion == null) return null
  return `${sym}${m.list_prompt == null ? '-' : num(m.list_prompt)} / ${sym}${m.list_completion == null ? '-' : num(m.list_completion)}`
}

export function ChannelModels() {
  const { t, locale } = useI18n()
  const toast = useToast()
  const { isAdmin } = useAdmin()
  const [channel, setChannel] = useState('')
  const [search, setSearch] = useState('')
  const [missingOnly, setMissingOnly] = useState(false)
  const channels = useLoad(() => catalogApi.channels(), [])
  const list = useLoad(() => catalogApi.libraryOverview(30, channel || undefined), [channel])
  const refStatus = useLoad(() => catalogApi.vendorRefs(), [])
  const [pricing, setPricing] = useState<LibraryItem | null>(null)
  const [editing, setEditing] = useState<LibraryItem | 'new' | null>(null)
  const [registering, setRegistering] = useState(false)
  const [deleting, setDeleting] = useState<LibraryItem | null>(null)
  const [busy, setBusy] = useState('')

  const items = list.data?.data ?? []
  const enabledItems = items.filter((m) => m.enabled)
  const missing = enabledItems.filter((m) => m.cost_missing).length
  const unknown = items.reduce((s, m) => s + m.unknown_cost_requests, 0)
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter((m) => (!q || m.upstream_model.toLowerCase().includes(q) || m.channel_id.toLowerCase().includes(q) || m.note.toLowerCase().includes(q)) && (!missingOnly || (m.cost_missing && m.enabled)))
  }, [items, search, missingOnly])

  const toggle = async (m: LibraryItem) => {
    setBusy(`${m.channel_id}/${m.upstream_model}`)
    try {
      const r = await catalogApi.patchLibraryItem(m.channel_id, m.upstream_model, { enabled: !m.enabled })
      toast.ok(t(G.library.toggled, { v: r.config_version }))
      list.reload()
    } catch (e) {
      console.error('[channel-models] 切换启用失败', e)
      toast.error(e)
    } finally {
      setBusy('')
    }
  }

  const remove = async () => {
    if (!deleting) return
    setBusy('delete')
    try {
      const r = await catalogApi.deleteChannelModel(deleting.channel_id, deleting.upstream_model)
      toast.ok(t(A.cm.deleted, { v: r.config_version }))
      setDeleting(null)
      list.reload()
    } catch (e) {
      console.error('[channel-models] 删除失败', e)
      toast.error(e)
    } finally {
      setBusy('')
    }
  }

  const refreshRefs = async () => {
    setBusy('refs')
    try {
      const r = await catalogApi.refreshVendorRefs()
      toast.ok(t(G.library.refsRefreshed, { n: r.items }))
    } catch (e) {
      console.error('[channel-models] 刷新公开刊例失败', e)
      toast.error(errorTextCat(e, locale))
    } finally {
      setBusy('')
      refStatus.reload()
    }
  }

  const src = refStatus.data?.sources[0]
  const refText = !src
    ? t(G.library.refsNever)
    : src.last_error
      ? t(G.library.refsError, { err: src.last_error })
      : t(G.library.refsStatus, { n: src.models, t: fmtDateTime(src.fetched_at, locale) })

  return (
    <div id="panel-providerModels" className="panel active">
      <InfoBox>{t(G.library.note)}</InfoBox>
      <SummaryGrid>
        <SummaryCard label={t(G.library.sTotal)} value={items.length.toLocaleString()} hint={channel ? channel : t(G.library.sTotalHint)} />
        <SummaryCard label={t(G.library.sPriced)} value={(enabledItems.length - missing).toLocaleString()} hint={t(G.library.sPricedHint)} />
        <SummaryCard label={t(G.library.sMissing)} value={missing.toLocaleString()} valueStyle={missing ? { color: 'var(--error-text)' } : undefined} hint={t(G.library.sMissingHint)} />
        <SummaryCard label={t(G.library.sUnknown)} value={unknown.toLocaleString()} hint={t(G.library.sUnknownHint)} />
      </SummaryGrid>

      {!isAdmin && (
        <div style={{ marginBottom: 12 }}>
          <Notice tone="accent">{t(G.common.readOnly)}</Notice>
        </div>
      )}
      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder={t(G.library.search)} width={220} />
        <select className="cat-select" value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="">{t(G.library.allChannels)}</option>
          {(channels.data?.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || c.id}
            </option>
          ))}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>
          <input type="checkbox" checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)} style={{ width: 15, height: 15, cursor: 'pointer' }} />
          {t(G.library.missingOnly)}
        </label>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: src?.last_error ? 'var(--error-text)' : 'var(--text-tertiary)' }}>{refText}</span>
        {isAdmin && (
          <button className="btn btn-secondary btn-sm" disabled={busy === 'refs'} onClick={refreshRefs} style={{ whiteSpace: 'nowrap' }}>
            <IconRefresh size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
            {t(G.library.refreshRefs)}
          </button>
        )}
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => setRegistering(true)} style={{ whiteSpace: 'nowrap' }}>
            <IconPlus size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
            {t(G.library.register)}
          </button>
        )}
      </Toolbar>

      <div className="table-wrap">
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <th>{t(G.library.colModel)}</th>
                <th>{t(G.library.colKind)}</th>
                <th>{t(G.library.colQuote)}</th>
                <th>{t(G.library.colCalls)}</th>
                <th title={t(G.library.colUnknownHint)}>{t(G.library.colUnknown)}</th>
                <th title={t(G.library.colRefsHint)}>{t(G.library.colRefs)}</th>
                <th>{t(G.library.colSource)}</th>
                <th>{t(G.library.colStatus)}</th>
                <th>{t(G.library.colActions)}</th>
              </tr>
            </thead>
            <tbody>
              {list.error ? (
                <tr>
                  <td colSpan={9} className="empty" style={{ textAlign: 'center', padding: 32, color: 'var(--error-text)' }}>
                    {t(G.common.loadFailed, { msg: errorTextCat(list.error, locale) })}
                  </td>
                </tr>
              ) : list.loading && !list.data ? (
                <tr>
                  <td colSpan={9} className="empty" style={{ textAlign: 'center', padding: 32 }}>
                    {t(G.common.loading)}
                  </td>
                </tr>
              ) : shown.length === 0 ? (
                <tr>
                  <td colSpan={9} className="empty" style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)' }}>
                    {t(G.library.empty)}
                  </td>
                </tr>
              ) : (
                shown.map((m) => {
                  const q = quoteText(m, t(A.models.imageModeToken), t(A.models.videoPricing))
                  const key = `${m.channel_id}/${m.upstream_model}`
                  return (
                    <tr key={key} className={m.cost_missing && m.enabled ? 'row-missing-cost' : undefined}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Logo name={m.channel_name || m.channel_id} size="sm" />
                          <div style={{ minWidth: 0 }}>
                            <div className="mono" style={{ fontWeight: 600, fontSize: 13 }}>
                              {m.upstream_model}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                              {m.channel_name || m.channel_id}
                              {m.context_length ? ` · ${fmtContext(m.context_length)}` : ''}
                              {m.note ? ` · ${m.note}` : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-info">{m.kind}</span>
                      </td>
                      <td>
                        {q ? (
                          <div>
                            <span className="mono">{q}</span>
                            {m.cost_discount != null && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}> ×{num(m.cost_discount)}</span>}
                          </div>
                        ) : (
                          <span className="badge badge-off">{t(A.cm.noQuote)}</span>
                        )}
                      </td>
                      <td>{m.requests.toLocaleString()}</td>
                      <td style={m.unknown_cost_requests ? { color: 'var(--error-text)' } : undefined}>{m.unknown_cost_requests.toLocaleString()}</td>
                      <td>{m.route_refs}</td>
                      <td>
                        <span className={`badge ${SOURCE_TONE[m.source] ?? 'badge-info'}`}>{t(G.library.source[m.source])}</span>
                      </td>
                      <td>
                        <span className={`badge ${m.enabled ? 'badge-ok' : 'badge-off'}`}>{t(m.enabled ? A.status.enabled : A.status.disabled)}</span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {isAdmin && (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => setPricing(m)}>
                              {t(G.library.editQuote)}
                            </button>
                            <ActionMenu label="···">
                              <button onClick={() => setEditing(m)}>
                                <IconEdit size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                                {t(G.library.fullEdit)}
                              </button>
                              <button disabled={busy === key} onClick={() => toggle(m)}>
                                {t(m.enabled ? G.library.disable : G.library.enable)}
                              </button>
                              <button style={{ color: 'var(--error-text)' }} onClick={() => setDeleting(m)}>
                                {t(C.delete)}
                              </button>
                            </ActionMenu>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="table-footer">{t(G.library.footer)}</div>
      </div>

      <QuoteModal
        item={pricing}
        onClose={() => setPricing(null)}
        onSaved={() => {
          setPricing(null)
          list.reload()
        }}
        onFull={(m) => {
          setPricing(null)
          setEditing(m)
        }}
      />
      <RegisterModal
        open={registering}
        channels={(channels.data?.data ?? []).map((c) => ({ id: c.id, name: c.name || c.id }))}
        defaultChannel={channel}
        onClose={() => setRegistering(false)}
        onSaved={(m) => {
          setRegistering(false)
          list.reload()
          const row = { channel_id: m.channel_id, upstream_model: m.upstream_model }
          // 登记后直接进入报价维护
          catalogApi
            .libraryOverview(30, row.channel_id)
            .then((r) => setPricing(r.data.find((x) => x.upstream_model === row.upstream_model) ?? null))
            .catch((e) => console.error('[channel-models] 登记后读取失败', e))
        }}
      />
      <ChannelModelForm
        item={editing}
        channelIds={(channels.data?.data ?? []).map((c) => c.id)}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          list.reload()
        }}
      />
      <ConfirmModal
        open={deleting !== null}
        danger
        busy={busy === 'delete'}
        title={t(A.cm.confirmDelete, { name: deleting ? `${deleting.channel_id}/${deleting.upstream_model}` : '' })}
        confirmLabel={t(C.delete)}
        onConfirm={remove}
        onClose={() => setDeleting(null)}
      >
        <span>{t(A.cm.inUse)}</span>
      </ConfirmModal>
    </div>
  )
}

/** 报价弹窗：刊例价与折扣，附公开刊例（OpenRouter）一键填入。复杂价目走「完整编辑」。 */
function QuoteModal({
  item,
  onClose,
  onSaved,
  onFull,
}: {
  item: LibraryItem | null
  onClose: () => void
  onSaved: () => void
  onFull: (m: LibraryItem) => void
}) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const [f, setF] = useState<Record<string, string>>({})
  const [currency, setCurrency] = useState<'USD' | 'CNY'>('USD')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [refs, setRefs] = useState<VendorRef[] | null>(null)
  const [shown, setShown] = useState<LibraryItem | null>(null)

  if (item !== shown) {
    setShown(item)
    setError('')
    setRefs(null)
    if (item) {
      setCurrency(item.price_currency)
      setF({
        list_prompt: num(item.list_prompt),
        list_completion: num(item.list_completion),
        list_cache_read: num(item.list_cache_read),
        list_cache_write: num(item.list_cache_write),
        list_per_image: num(item.list_per_image),
        cost_discount: num(item.cost_discount),
      })
    }
  }

  useEffect(() => {
    if (!item) return
    let alive = true
    catalogApi
      .vendorRefs(item.upstream_model, 6)
      .then((r) => alive && setRefs(r.items))
      .catch((e) => {
        console.error('[channel-models] 读取公开刊例失败', e)
        if (alive) setRefs([])
      })
    return () => {
      alive = false
    }
  }, [item])

  const set = (k: string) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }))
  const kind: ModelKind | undefined = item?.kind
  const textual = kind === 'chat' || kind === 'embedding' || kind === 'speech' || kind === 'transcription'
  const quick = textual || (kind === 'image' && item?.image_billing_mode === 'per_image')

  const fill = (r: VendorRef) => {
    setCurrency(r.currency === 'CNY' ? 'CNY' : 'USD')
    setF((x) => ({
      ...x,
      list_prompt: num(r.prompt) || x.list_prompt,
      list_completion: num(r.completion) || x.list_completion,
      list_cache_read: num(r.cache_read) || x.list_cache_read,
      list_cache_write: num(r.cache_write) || x.list_cache_write,
      list_per_image: num(r.per_image) || x.list_per_image,
    }))
  }

  const submit = async () => {
    if (!item) return
    setBusy(true)
    setError('')
    try {
      const body: LibraryPatch = { price_currency: currency, cost_discount: orNull(f.cost_discount ?? '') }
      if (textual) {
        body.list_prompt = orNull(f.list_prompt ?? '')
        body.list_completion = orNull(f.list_completion ?? '')
        body.list_cache_read = orNull(f.list_cache_read ?? '')
        body.list_cache_write = orNull(f.list_cache_write ?? '')
      } else if (kind === 'image') {
        body.list_per_image = orNull(f.list_per_image ?? '')
      }
      const r = await catalogApi.patchLibraryItem(item.channel_id, item.upstream_model, body)
      toast.ok(t(A.models.savedVersion, { v: r.config_version }))
      onSaved()
    } catch (e) {
      console.error('[channel-models] 保存报价失败', e)
      setError(errorTextCat(e, locale))
    } finally {
      setBusy(false)
    }
  }

  const price = (k: string, label: string) => (
    <Field label={label}>
      <Input inputMode="decimal" pattern="[0-9]+(\.[0-9]+)?" value={f[k] ?? ''} onChange={set(k)} />
    </Field>
  )

  return (
    <Modal open={item !== null} onClose={onClose} busy={busy} wide title={t(G.library.quoteTitle, { name: item ? `${item.channel_id}/${item.upstream_model}` : '' })}>
      <Form onSubmit={submit}>
        <p className="ap-muted" style={{ margin: 0 }}>
          {t(G.library.quoteLead)}
        </p>
        {error && <Notice tone="danger">{error}</Notice>}
        {!quick && <Notice tone="warning">{t(G.library.quoteComplex)}</Notice>}
        {quick && (
          <>
            <div className="ap-grid2">
              <Field label={t(A.models.currency)}>
                <Select value={currency} onChange={(e) => setCurrency(e.target.value as 'USD' | 'CNY')} options={[{ value: 'USD', label: 'USD' }, { value: 'CNY', label: 'CNY' }]} />
              </Field>
              <Field label={t(A.cm.discount)} hint={t(A.cm.discountHint)}>
                <Input inputMode="decimal" pattern="[0-9]+(\.[0-9]{1,6})?" value={f.cost_discount ?? ''} onChange={set('cost_discount')} />
              </Field>
            </div>
            {textual && (
              <div className="ap-grid2">
                {price('list_prompt', t(A.cm.listPrompt))}
                {price('list_completion', t(A.cm.listCompletion))}
                {price('list_cache_read', t(A.cm.listCacheRead))}
                {price('list_cache_write', t(A.cm.listCacheWrite))}
              </div>
            )}
            {kind === 'image' && <div className="ap-grid2">{price('list_per_image', t(A.cm.listPerImage))}</div>}
          </>
        )}
        <div>
          <div className="cat-field-label">{t(G.library.refsTitle)}</div>
          {refs === null ? (
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t(G.common.loading)}</span>
          ) : refs.length === 0 ? (
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t(G.library.refsNone)}</span>
          ) : (
            <div style={{ border: '1px solid var(--border-medium)', borderRadius: 6 }}>
              {refs.map((r) => (
                <div key={`${r.source}/${r.ref_id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="mono" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.ref_id}
                    </div>
                    <div style={{ color: 'var(--text-tertiary)' }}>
                      {r.currency} · {t(G.library.refsPrice, { p: num(r.prompt) || '-', c: num(r.completion) || '-' })}
                      {r.context_length ? ` · ${fmtContext(r.context_length)}` : ''}
                    </div>
                  </div>
                  {quick && (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => fill(r)}>
                      {t(G.library.refsFill)}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="ap-row" style={{ justifyContent: 'space-between' }}>
          <Button onClick={() => item && onFull(item)} disabled={busy}>
            {t(G.library.fullEdit)}
          </Button>
          <div className="ap-row">
            <Button onClick={onClose} disabled={busy}>
              {t(C.cancel)}
            </Button>
            <Button type="submit" variant="primary" loading={busy} disabled={!quick}>
              {t(C.save)}
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  )
}

/** 登记上游模型：选渠道后自动拉目录做候选；登记后返回条目，页面直接打开报价弹窗。 */
function RegisterModal({
  open,
  channels,
  defaultChannel,
  onClose,
  onSaved,
}: {
  open: boolean
  channels: { id: string; name: string }[]
  defaultChannel: string
  onClose: () => void
  onSaved: (m: { channel_id: string; upstream_model: string }) => void
}) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const [channel, setChannel] = useState('')
  const [model, setModel] = useState('')
  const [kind, setKind] = useState<ModelKind>('chat')
  const [ctx, setCtx] = useState('')
  const [catalog, setCatalog] = useState<FetchedModel[] | null>(null)
  const [hint, setHint] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [wasOpen, setWasOpen] = useState(false)

  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setChannel(defaultChannel || channels[0]?.id || '')
      setModel('')
      setKind('chat')
      setCtx('')
      setCatalog(null)
      setHint('')
      setError('')
    }
  }

  useEffect(() => {
    if (!open || !channel) return
    let alive = true
    setHint(t(G.library.regFetching))
    catalogApi
      .fetchChannelModels(channel)
      .then((r) => {
        if (!alive) return
        setCatalog(r.models)
        setHint(t(G.library.regFetched, { n: r.models.length }))
      })
      .catch((e) => {
        console.warn('[channel-models] 拉取渠道模型目录失败', e)
        if (!alive) return
        setCatalog([])
        setHint(t(G.library.regFetchFailed, { msg: errorTextCat(e, locale) }))
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, channel])

  const pick = catalog?.find((m) => m.model_id === model.trim())
  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const upstream = model.trim()
      const body = {
        kind,
        note: '',
        price_currency: 'USD',
        list_prompt: null,
        list_completion: null,
        list_cache_read: null,
        list_cache_write: null,
        list_per_image: null,
        image_billing_mode: 'per_image',
        image_token_prices: {},
        video_pricing: {},
        peak_pricing: null,
        cost_discount: null,
        context_length: ctx.trim() ? Number(ctx) : (pick?.context_length ?? null),
        enabled: true,
        source: pick ? 'fetched' : 'manual',
      } as ChannelModelInput
      const r = await catalogApi.putChannelModel(channel, upstream, body)
      toast.ok(t(A.models.savedVersion, { v: r.config_version }))
      onSaved({ channel_id: channel, upstream_model: upstream })
    } catch (e) {
      console.error('[channel-models] 登记失败', e)
      setError(errorTextCat(e, locale))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} busy={busy} title={t(G.library.regTitle)}>
      <Form onSubmit={submit}>
        <p className="ap-muted" style={{ margin: 0 }}>
          {t(G.library.regLead)}
        </p>
        {error && <Notice tone="danger">{error}</Notice>}
        <Field label={t(A.cm.channel)}>
          <Select required value={channel} onChange={(e) => setChannel(e.target.value)} options={channels.map((c) => ({ value: c.id, label: c.name }))} />
        </Field>
        <Field label={t(A.cm.upstreamModel)} hint={hint}>
          <Input mono required list="cat-reg-models" maxLength={200} autoComplete="off" value={model} onChange={(e) => setModel(e.target.value)} />
          <datalist id="cat-reg-models">
            {(catalog ?? []).map((m) => (
              <option key={m.model_id} value={m.model_id}>
                {m.registered ? t(G.providers.registered) : ''}
              </option>
            ))}
          </datalist>
        </Field>
        <div className="ap-grid2">
          <Field label={t(A.models.kind)}>
            <Select value={kind} onChange={(e) => setKind(e.target.value as ModelKind)} options={KINDS.map((k) => ({ value: k, label: k }))} />
          </Field>
          <Field label={t(A.cm.context)}>
            <Input type="number" min={1} value={ctx} placeholder={pick?.context_length ? String(pick.context_length) : ''} onChange={(e) => setCtx(e.target.value)} />
          </Field>
        </div>
        <div className="ap-row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={busy}>
            {t(C.cancel)}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t(G.library.regSubmit)}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

